import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Loader2, Package } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { productApi } from '../../services/api';
import { normalizeProduct } from '../../services/catalogService';
import { useToast } from '../hooks/useToast';

const getItemStatus = (isActive, stock) => {
  if (isActive === false) return 'inactive';
  if (Number(stock || 0) > 0 && Number(stock || 0) <= 10) return 'low_stock';
  return 'active';
};

const buildProductRows = (products) =>
  products.flatMap((product) => {
    const normalized = normalizeProduct(product);
    const baseRow = {
      ...normalized,
      id: normalized.id,
      parentId: normalized.id,
      itemType: 'Product',
      rowTitle: normalized.name,
      rowSubtitle: normalized.sku,
      image: normalized.image,
      colorDisplay: normalized.colorName || normalized.color || '-',
      sizeDisplay: '-',
      status: getItemStatus(product.isActive, product.stock)
    };

    const variantRows = (normalized.variants || []).map((variant, index) => {
      const variantId = variant.id || variant._id || variant.sku || index;
      const variantName = variant.name || [variant.colorName || variant.color, variant.size].filter(Boolean).join(' / ') || `Variant ${index + 1}`;
      const variantImage = variant.colorImage || variant.images?.[0] || normalized.image;

      return {
        ...normalized,
        ...variant,
        id: `${normalized.id}::variant::${variantId}`,
        parentId: normalized.id,
        parentProduct: normalized,
        itemType: 'Variant',
        rowTitle: normalized.name,
        rowSubtitle: variantName,
        image: variantImage,
        sku: variant.sku || normalized.sku,
        category: normalized.category,
        price: variant.price ?? normalized.price,
        marketPrice: variant.marketPrice ?? normalized.marketPrice,
        stock: variant.stock ?? normalized.stock,
        rating: normalized.rating,
        colorDisplay: variant.colorName || variant.color || normalized.colorName || normalized.color || '-',
        sizeDisplay: variant.size || '-',
        status: getItemStatus(product.isActive, variant.stock ?? normalized.stock)
      };
    });

    return [baseRow, ...variantRows];
  });

const Products = () => {
  const navigate = useNavigate();
  const { success, error: showError, warning } = useToast();
  const [productList, setProductList] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await productApi.list({ limit: 200 });
      const nextProducts = buildProductRows(response.data?.data || []);
      setProductList(nextProducts);
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to load products';
      setError(msg);
      warning(msg);
    } finally {
      setLoading(false);
    }
  }, [warning]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleEdit = (product) => {
    const parentProduct = product.parentProduct || product;
    navigate(`/admin/products/edit/${product.parentId || product.id}`, { state: { product: parentProduct } });
  };

  const handleDelete = (id) => {
    if (String(id).includes('::variant::')) {
      warning('Open the parent product and remove this variant from the Variants step.');
      return;
    }
    setSelectedProduct(id);
    setConfirmDelete(true);
  };

  const confirmDeleteProduct = async () => {
    try {
      setDeleting(true);
      await productApi.delete(selectedProduct);
      setProductList((currentProducts) => currentProducts.filter((product) => product.parentId !== selectedProduct));
      success('Product deleted successfully');
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to delete product';
      showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Item',
      render: (val, row) => (
        <div className="flex min-w-[260px] items-center gap-3">
          <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
            {row.image ? (
              <img src={row.image} alt={row.rowTitle} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-gray-400">
                <Package size={18} />
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">{row.rowTitle}</p>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                row.itemType === 'Variant' ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'
              }`}>
                {row.itemType}
              </span>
            </div>
            <p className="text-xs text-gray-500">{row.rowSubtitle}</p>
            <p className="text-xs text-gray-400">SKU: {row.sku || '-'}</p>
          </div>
        </div>
      ),
      sortable: true
    },
    { key: 'category', label: 'Category', sortable: true },
    {
      key: 'colorDisplay',
      label: 'Colour / Size',
      render: (val, row) => (
        <div className="min-w-[130px]">
          <p className="font-medium text-gray-900">{val}</p>
          <p className="text-xs text-gray-500">{row.sizeDisplay}</p>
        </div>
      ),
      sortable: true
    },
    {
      key: 'price',
      label: 'Price',
      render: (val, row) => (
        <div>
          <p className="font-medium text-gray-900">Rs {Number(val || 0).toLocaleString()}</p>
          {row.marketPrice > row.price && (
            <p className="text-xs text-gray-500 line-through">Rs {Number(row.marketPrice).toLocaleString()}</p>
          )}
        </div>
      ),
      sortable: true
    },
    { key: 'stock', label: 'Stock', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (val) => {
        const status = val || 'active';
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            status === 'active'
              ? 'bg-green-100 text-green-700'
              : status === 'inactive'
                ? 'bg-gray-100 text-gray-700'
                : 'bg-yellow-100 text-yellow-700'
          }`}>
            {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
          </span>
        );
      }
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (val) => (
        <div className="flex items-center gap-1">
          <span className="text-yellow-400">*</span>
          <span className="font-medium">{Number(val || 0).toFixed(1)}</span>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Products"
          description="Manage your furniture catalog"
          breadcrumbs={['Home', 'Products']}
          action={
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/admin/products/add')}
              className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition font-medium"
            >
              <Plus size={18} />
              Add Product
            </motion.button>
          }
        />

        {error && <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
            <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
            <span className="text-gray-600">Loading products...</span>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={productList}
            onEdit={handleEdit}
            onDelete={handleDelete}
            searchable={true}
            paginate={true}
            itemsPerPage={10}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={confirmDeleteProduct}
        title="Delete Product"
        message="Are you sure you want to delete this product?"
        confirmText={deleting ? "Deleting..." : "Delete"}
        isDangerous={true}
      />
    </>
  );
};

export default Products;
